// Renders a verbatim source clause as a paper-document snippet —
// serif body, paper-cream surface, citation header, footer line
// telling the reader what instrument it came from. Used in the
// Source panel of every Value and in Sections H / I / J wherever
// a source_text excerpt is shown, so the planner can verify
// without opening the underlying PDF.
//
// Deliberately not designed to look like a forged page (no fake
// letterhead / logos) — the footer "Excerpt — {source}, {citation}"
// makes it clear this is a presentation of text we lifted from the
// instrument, not the instrument's page itself.
export function ClauseSnippet({
  text,
  source,
  citation,
  truncate,
}: {
  text: string
  source?: string | null
  citation?: string | null
  // When set, truncate the visible body at this character count
  // with a "show more" toggle. Useful in dense lists like Section I
  // encumbrances where every row might carry a paragraph excerpt.
  truncate?: number
}) {
  const trimmed = text.trim()
  if (!trimmed) return null
  const tooLong = truncate != null && trimmed.length > truncate
  const visible = tooLong ? trimmed.slice(0, truncate) + '…' : trimmed
  const footerBits: string[] = []
  if (source) footerBits.push(source)
  if (citation) footerBits.push(citation)
  return (
    <div
      className="rounded-md border border-massing-rule bg-massing-paper-deep relative overflow-hidden"
      style={{
        boxShadow: '0 1px 2px rgba(0,0,0,0.10), 0 4px 12px rgba(0,0,0,0.05)',
      }}
    >
      {/* faint top-edge gradient to hint at paper depth */}
      <div
        className="absolute inset-x-0 top-0 h-3 pointer-events-none"
        style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.05), transparent)' }}
        aria-hidden
      />
      <div
        className="font-serif text-[13.5px] leading-[1.55] text-massing-ink whitespace-pre-wrap px-4 py-3.5 relative"
        style={{
          fontFamily: "Georgia, 'Times New Roman', Times, serif",
          textAlign: 'justify',
          hyphens: 'auto',
        }}
      >
        {/* Decorative open-quote in the gutter — makes the snippet
            unmistakably a quote of the source rather than our own
            interpretation. */}
        <span
          className="absolute font-serif text-massing-ink-light/35 select-none pointer-events-none"
          style={{ left: '0.25rem', top: '-0.1rem', fontSize: '2.6rem', lineHeight: 1 }}
          aria-hidden
        >
          “
        </span>
        <span className="relative pl-3">{visible}</span>
      </div>
      {footerBits.length > 0 && (
        <div className="border-t border-massing-rule-soft px-4 py-1.5 text-[10.5px] tracking-[0.04em] uppercase text-massing-ink-light bg-massing-cream-deep/40">
          Excerpt — {footerBits.join(', ')}
        </div>
      )}
    </div>
  )
}
